import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
    isPlainMachineTranslation,
    type TranslationLanguage
} from '@ast-data/shared';
import { useAuth } from '../auth/useAuth';
import { useConfirm } from '../../components/confirmContext';
import { useRecordTranslationsScope } from './recordTranslations';
import { translationContext } from './translationContext';
import {
    requestTranslation,
    TranslationRequestError,
    useTranslationAvailability,
    type TranslationFailure
} from './translationApi';
import {
    detectLanguage,
    looksForeign,
    normalizeSourceLanguage,
    otherLanguage,
    translationStatus,
    type TranslationContent,
    type TranslationStatus
} from './translationState';

/** Why the translate action is off, for its tooltip — or null when it is on. */
export type TranslateBlocker =
    'offline' | 'empty' | 'unsaved' | 'reviewed' | 'busy' | null;

export interface FieldTranslation {
    translation: TranslationContent | null;
    status: TranslationStatus | null;
    /** May edit, approve and discard — i.e. may change the record. */
    canReview: boolean;
    /** The server has DeepL; without it the field shows no translate button. */
    serviceEnabled: boolean;
    blocker: TranslateBlocker;
    busy: boolean;
    error: TranslationFailure | null;
    /**
     * The languages worth offering. One when the text's language is clear —
     * translating German into German is no translation — both when it is not.
     */
    targets: TranslationLanguage[];
    /**
     * Resolves true when a translation came back. `text` is the field's text
     * when it has just changed and the form has not re-rendered yet.
     */
    translate(targetLang: TranslationLanguage, text?: string): Promise<boolean>;
    /** Take in the German of a dictation — see `addDictated` below. */
    addDictated(dictated: DictatedTranslation): Promise<void>;
    edit(text: string): void;
    approve(): void;
    discard(): Promise<void>;
}

/** A dictation that came back translated into German (DeepL Voice). */
export interface DictatedTranslation {
    /** The field's text before the dictation went in. */
    previousText: string;
    /** …and after. */
    nextText: string;
    /** Where in `previousText` the dictation went in. */
    insertAt: number;
    /** The German of what was said. */
    translation: string;
    /** The language it was said in. */
    sourceLang: string;
}

/**
 * Everything one long-text field needs to translate its value. Null outside a
 * form that tracks translations (see useRecordTranslations), so the field
 * shows no translation tools at all there.
 */
export function useFieldTranslation(
    field: string,
    currentText: string
): FieldTranslation | null {
    const intl = useIntl();
    const scope = useRecordTranslationsScope();
    const { enabled, online } = useTranslationAvailability();
    const { user } = useAuth();
    const { confirm } = useConfirm();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<TranslationFailure | null>(null);
    // Runs on every keystroke, so on the opening only: that is where the
    // language shows, and it keeps a 20 000-character text cheap.
    const detected = useMemo(
        () => (scope ? detectLanguage(currentText.slice(0, 1500)) : null),
        [scope, currentText]
    );

    if (!scope) {
        return null;
    }

    const translation = scope.get(field);
    const status = translation
        ? translationStatus(translation, currentText)
        : null;
    const canReview = !scope.readOnly;
    const fallbackTarget =
        (translation?.targetLang as TranslationLanguage | undefined) ?? 'en';
    const targets: TranslationLanguage[] = detected
        ? [otherLanguage(detected)]
        : [fallbackTarget, otherLanguage(fallbackTarget)];

    let blocker: TranslateBlocker = null;
    if (busy) {
        blocker = 'busy';
    } else if (!online) {
        blocker = 'offline';
    } else if (currentText.trim() === '') {
        blocker = 'empty';
    } else if (scope.readOnly && !scope.ownerId) {
        blocker = 'unsaved';
    } else if (
        !canReview &&
        translation &&
        !isPlainMachineTranslation(translation)
    ) {
        // A reader may not throw reviewed work away; the server would refuse.
        blocker = 'reviewed';
    }

    function confirmReplace() {
        return confirm({
            title: intl.formatMessage({ id: 'translation.replace.title' }),
            message: intl.formatMessage({ id: 'translation.replace.message' }),
            confirmLabel: intl.formatMessage({
                id: 'translation.replace.confirm'
            })
        });
    }

    async function translate(
        targetLang: TranslationLanguage,
        text: string = currentText
    ) {
        if (!scope || blocker) {
            return false;
        }
        // Reviewed work is not thrown away without asking.
        if (
            translation &&
            !isPlainMachineTranslation(translation) &&
            !(await confirmReplace())
        ) {
            return false;
        }
        setBusy(true);
        setError(null);
        try {
            const result = await requestTranslation({
                text,
                targetLang,
                projectId: scope.projectId,
                context: translationContext(scope.ownerType, field)
            });
            // The backstop for a text too short to tell: DeepL hands it back
            // unchanged, and storing that would label it machine-translated.
            // The source is named in the request, so the text says more than
            // the language DeepL reports.
            if (
                result.text.trim() === text.trim() ||
                result.sourceLang.toLowerCase().startsWith(targetLang)
            ) {
                setError('sameLanguage');
                return false;
            }
            await scope.set(field, {
                sourceLang: normalizeSourceLanguage(
                    result.sourceLang,
                    targetLang
                ),
                targetLang,
                sourceText: text,
                text: result.text,
                origin: 'machine',
                approvedBy: '',
                approvedAt: ''
            });
            return true;
        } catch (failure) {
            setError(
                failure instanceof TranslationRequestError
                    ? failure.reason
                    : 'unavailable'
            );
            return false;
        } finally {
            setBusy(false);
        }
    }

    /**
     * The German of a dictation becomes the field's translation — machine
     * text, to be accepted like any other — so that the translation always
     * covers the whole field:
     *
     * - the field was empty: the German is the translation;
     * - a current German translation and the dictation went in at the end:
     *   the German is appended to it;
     * - the text before was German itself (no translation into German, but
     *   one into English, or none and it reads German): its German is the
     *   text, and the dictated German goes in at the same place. A field has
     *   ONE translation, so this replaces one into English: a plain machine
     *   one silently (a click makes it again), a reviewed one only on a yes
     *   — on a no it stays and goes stale, as after any change;
     * - anything else (English or a third language before, a German
     *   translation the insert cannot be placed in): the whole field is
     *   translated afresh.
     */
    async function addDictated({
        previousText,
        nextText,
        insertAt,
        translation: dictated,
        sourceLang
    }: DictatedTranslation) {
        if (!scope || !canReview || dictated.trim() === '') {
            return;
        }
        const machine = {
            sourceLang,
            targetLang: 'de' as const,
            sourceText: nextText,
            origin: 'machine',
            approvedBy: '',
            approvedAt: ''
        };
        if (previousText.trim() === '') {
            await scope.set(field, { ...machine, text: dictated });
            return;
        }
        const before = previousText.slice(0, insertAt);
        const after = previousText.slice(insertAt);
        const currentGerman =
            translation?.targetLang === 'de' &&
            translationStatus(translation, previousText) !== 'stale';
        if (currentGerman && after.trim() === '') {
            const separator = /\s$/.test(translation.text) ? '' : ' ';
            await scope.set(field, {
                ...translation,
                sourceText: nextText,
                text: `${translation.text}${separator}${dictated}`,
                // What was accepted is no longer all there is.
                approvedBy: '',
                approvedAt: ''
            });
            return;
        }
        const previousIsGerman = translation
            ? translation.targetLang !== 'de' && translation.sourceLang === 'de'
            : detectLanguage(previousText) !== 'en' &&
              !looksForeign(previousText);
        if (previousIsGerman) {
            // Reviewed work into the other language goes only on a yes, as
            // with the translate button. On a no the field keeps it, stale.
            if (
                translation &&
                !isPlainMachineTranslation(translation) &&
                !(await confirmReplace())
            ) {
                return;
            }
            const separator =
                before.length > 0 && !/\s$/.test(before) ? ' ' : '';
            await scope.set(field, {
                ...machine,
                text: `${before}${separator}${dictated} ${after}`.trimEnd()
            });
            return;
        }
        await translate('de', nextText);
    }

    function edit(text: string) {
        // The same text again is no edit — an editor may report it unasked.
        if (!scope || !translation || !canReview || text === translation.text) {
            return;
        }
        // Editing withdraws an approval: what was approved is no longer there.
        void scope.set(field, {
            ...translation,
            text,
            origin: 'edited',
            approvedBy: '',
            approvedAt: ''
        });
    }

    function approve() {
        if (!scope || !translation || !canReview || status === 'stale') {
            return;
        }
        void scope.set(field, {
            ...translation,
            approvedBy: user?.id ?? '',
            approvedAt: new Date().toISOString()
        });
    }

    async function discard() {
        if (!scope || !translation || !canReview) {
            return;
        }
        const confirmed = await confirm({
            title: intl.formatMessage({ id: 'translation.discard.title' }),
            message: intl.formatMessage({ id: 'translation.discard.message' }),
            confirmLabel: intl.formatMessage({
                id: 'translation.discard.confirm'
            }),
            confirmColor: 'error'
        });
        if (confirmed) {
            await scope.set(field, null);
        }
    }

    return {
        translation,
        status,
        canReview,
        serviceEnabled: enabled,
        blocker,
        busy,
        error,
        targets,
        translate,
        addDictated,
        edit,
        approve,
        discard
    };
}
